import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../styleSheets/plans.css";
import "../styleSheets/buttons.css";
import SocialResponsibility from "./SocialResponsibility";
import messagesIcon from "../images/messages.svg";
import recipientsIcon from "../images/recipients.svg";
import attachmentsIcon from "../images/attachments.svg";
import { useUser } from "../contexts/UserContext";
import {
  BillingPlan,
  BillingUser,
  SELECTED_PLAN_STORAGE_KEY,
  createBillingCheckoutSession,
  getBillingLifecycleState,
  getPlans,
  getUser,
  isActivePlan,
  isFreePlan,
  isPaidPlan,
  listBillingPlans,
  logBillingEvent,
} from "../utilities/billing";
import { formatCurrency } from "../utilities/utilities";
import { getLocalizedPlan } from "../utilities/publicContent";

import {
  fetchLegacyMessagesByUserId,
  fetchRecipientsByUserId,
  fetchAttachmentsByUserId,
} from "../graphql/fetchers";
import { TooltipButton } from "../utilities/ToolTipButton";

const getPlanId = (plan?: BillingPlan | null): string =>
  (plan?.planId ?? plan?.id ?? "").trim();

const formatDateTime = (value?: string | null): string | null => {
  const normalizedValue = (value ?? "").trim();
  if (!normalizedValue) return null;

  const parsed = new Date(normalizedValue);
  return Number.isNaN(parsed.getTime())
    ? normalizedValue
    : parsed.toLocaleString();
};

function toLimit(value: any): number {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return 0;
  return n;
}

function isUnlimited(limit: number): boolean {
  return limit >= 9999;
}

type UsageCounts = {
  messages: number;
  recipients: number;
  attachments: number;
};

function Plans() {
  const { t } = useTranslation(["common", "plans"]);
  const navigate = useNavigate();
  const { isAuthenticated, user } = useUser();

  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [userRecord, setUserRecord] = useState<BillingUser | null>(null);
  const [usage, setUsage] = useState<UsageCounts>({
    messages: 0,
    recipients: 0,
    attachments: 0,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyPlanId, setBusyPlanId] = useState<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();

    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const uid = (user?.userId ?? "").trim();
        const isSignedInUser = isAuthenticated && !!uid;

        const [planList, currentUser, messages, recipients, attachments] =
          await Promise.all([
            isSignedInUser ? listBillingPlans() : getPlans(ac.signal),
            isSignedInUser ? getUser(uid) : Promise.resolve(null),
            isSignedInUser
              ? fetchLegacyMessagesByUserId(uid)
              : Promise.resolve([]),
            isSignedInUser
              ? fetchRecipientsByUserId(uid)
              : Promise.resolve([]),
            isSignedInUser
              ? fetchAttachmentsByUserId(uid)
              : Promise.resolve([]),
          ]);

        if (ac.signal.aborted) return;

        const visiblePlans = [...planList]
          .filter((plan) => !isSignedInUser || isActivePlan(plan))
          .sort((a, b) =>
            (a.name ?? "").localeCompare(b.name ?? "", undefined, {
              sensitivity: "base",
            }),
          );

        setPlans(visiblePlans);
        setUserRecord(currentUser);
        setUsage({
          messages: Array.isArray(messages) ? messages.length : 0,
          recipients: Array.isArray(recipients) ? recipients.length : 0,
          attachments: Array.isArray(attachments) ? attachments.length : 0,
        });
      } catch (e: any) {
        if (e?.name === "AbortError") return;
        setError(e?.message ?? "Failed to load plans");
      } finally {
        if (!ac.signal.aborted) setIsLoading(false);
      }
    };

    void load();
    return () => ac.abort();
  }, [isAuthenticated, user?.userId]);

  const currentPlan = useMemo(() => {
    const currentPlanId = (userRecord?.userPlanId ?? "").trim();
    if (!currentPlanId) return null;
    return plans.find((plan) => getPlanId(plan) === currentPlanId) ?? null;
  }, [plans, userRecord?.userPlanId]);

  const pendingPlanId = (userRecord?.pendingPlanId ?? "").trim();
  const hasPendingPaidCheckout = useMemo(() => {
    if (!pendingPlanId) return false;
    if (!currentPlan || !isFreePlan(currentPlan)) return false;

    const pendingPlan =
      plans.find((plan) => getPlanId(plan) === pendingPlanId) ?? null;
    return !!pendingPlan && isPaidPlan(pendingPlan);
  }, [currentPlan, pendingPlanId, plans]);
  const scheduledPlan = useMemo(() => {
    const scheduledPlanId = (userRecord?.scheduledPlanId ?? "").trim();
    if (!scheduledPlanId) return null;
    return plans.find((plan) => getPlanId(plan) === scheduledPlanId) ?? null;
  }, [plans, userRecord?.scheduledPlanId]);
  const billingState = useMemo(
    () => getBillingLifecycleState(userRecord),
    [userRecord],
  );
  const billingEffectiveAtLabel = useMemo(
    () => formatDateTime(billingState.billingEffectiveAt),
    [billingState.billingEffectiveAt],
  );
  const billingNotices = useMemo(() => {
    const notices: Array<{ tone: "warning" | "info"; message: string }> = [];

    if (billingState.hasScheduledDowngrade && billingEffectiveAtLabel) {
      notices.push({
        tone: "info",
        message: t("checkout.onScreenMessage.planScheduledToChange", {
          planName:
            scheduledPlan?.name ?? userRecord?.scheduledPlanId ?? "the selected plan",
          dateTime: billingEffectiveAtLabel,
        }),
      });
    }

    if (billingState.hasPaymentFailure) {
      notices.push({
        tone: "warning",
        message: billingEffectiveAtLabel
          ? t("checkout.onScreenMessage.paymentFailedUntil", {
              dateTime: billingEffectiveAtLabel,
            })
          : t("checkout.onScreenMessage.paymentFailedChoosePlan"),
      });
    }

    if (billingState.isCanceledAtPeriodEnd && billingEffectiveAtLabel) {
      notices.push({
        tone: "info",
        message: t("checkout.onScreenMessage.subscriptionCanceledUntil", {
          dateTime: billingEffectiveAtLabel,
        }),
      });
    }

    return notices;
  }, [
    billingEffectiveAtLabel,
    billingState.hasPaymentFailure,
    billingState.hasScheduledDowngrade,
    billingState.isCanceledAtPeriodEnd,
    scheduledPlan?.name,
    userRecord?.scheduledPlanId,
    t,
  ]);

  const handleSelectPlan = async (plan: BillingPlan) => {
    const selectedPlanId = getPlanId(plan);
    if (!selectedPlanId) return;

    if (!isAuthenticated || !user?.userId) {
      window.localStorage.setItem(SELECTED_PLAN_STORAGE_KEY, selectedPlanId);
      navigate(`/Register?planId=${encodeURIComponent(selectedPlanId)}`);
      return;
    }

    const currentPlanId = getPlanId(currentPlan);
    const isClearingPendingFreeSelection =
      hasPendingPaidCheckout &&
      !!currentPlan &&
      isFreePlan(currentPlan) &&
      currentPlanId &&
      currentPlanId === selectedPlanId;

    if (
      currentPlanId &&
      currentPlanId === selectedPlanId &&
      !isClearingPendingFreeSelection
    ) {
      return;
    }

    try {
      setBusyPlanId(selectedPlanId);
      setError(null);

      await logBillingEvent({
        userId: user.userId,
        eventType: "PLAN_SELECTED",
        metadata: {
          fromPlanId: pendingPlanId || currentPlanId || null,
          toPlanId: selectedPlanId,
          planId: selectedPlanId,
          selectedAt: new Date().toISOString(),
        },
        status: "PENDING",
      });

      if (isClearingPendingFreeSelection) {
        const result = await createBillingCheckoutSession({
          planId: selectedPlanId,
          action: "clear_pending_checkout",
        });

        if (result.action !== "clear_pending_checkout") {
          setError(t("checkout.errorMessage.expectedClearedPendingCheckout"));
          return;
        }

        window.localStorage.removeItem(SELECTED_PLAN_STORAGE_KEY);
        navigate("/Dashboard");
        return;
      }

      window.localStorage.setItem(SELECTED_PLAN_STORAGE_KEY, selectedPlanId);

      if (
        hasPendingPaidCheckout &&
        !!currentPlan &&
        isFreePlan(currentPlan) &&
        isPaidPlan(plan)
      ) {
        const result = await createBillingCheckoutSession({
          planId: selectedPlanId,
          action: "defer_checkout",
        });

        if (result.action !== "defer_checkout") {
          setError(t("checkout.errorMessage.expectedDeferredCheckout"));
          return;
        }
      }

      navigate(`/Checkout?planId=${encodeURIComponent(selectedPlanId)}`);
    } catch (e: any) {
      setError(e?.message ?? t("checkout.errorMessage.unableSelectPlan"));
    } finally {
      setBusyPlanId(null);
    }
  };

  const isPlanBelowUsage = (plan: BillingPlan): boolean => {
    if (!isAuthenticated) return false;

    const msgLimit = toLimit((plan as any).messagesAllowed);
    const recLimit = toLimit((plan as any).recipientsAllowed);
    const attLimit = toLimit((plan as any).attachmentsAllowed);

    const blocksMessages = !isUnlimited(msgLimit) && usage.messages > msgLimit;
    const blocksRecipients =
      !isUnlimited(recLimit) && usage.recipients > recLimit;
    const blocksAttachments =
      !isUnlimited(attLimit) && usage.attachments > attLimit;

    return blocksMessages || blocksRecipients || blocksAttachments;
  };

  return (
    <>
      <section className="section-plans" aria-labelledby="plans-page-title">
        <div className="plans-container">
          <h1 id="plans-page-title" className="plans-title">
            {t("header.menu.pricing")}
          </h1>
          <div className="plans-hero__section">
            <p className="plans-hero__lead">{t("pageBody.plans.heroLead")}</p>
            <p className="plans-hero__text">{t("pageBody.plans.heroText")}</p>
          </div>

          {isLoading ? (
            <div className="plans-loading">{t("common.loading")}</div>
          ) : error ? (
            <div className="plans-error" style={{ color: "#ef4130" }}>
              {error}
            </div>
          ) : plans.length === 0 ? (
            <div className="plans-empty">
              {t("header.errorMessage.noPlans")}
            </div>
          ) : (
            <>
              {billingNotices.length > 0 ? (
                <div
                  style={{
                    marginBottom: 20,
                    display: "grid",
                    gap: 10,
                  }}
                >
                  {billingNotices.map((notice) => (
                    <div
                      key={notice.message}
                      style={{
                        borderRadius: 12,
                        padding: "14px 16px",
                        border:
                          notice.tone === "warning"
                            ? "1px solid rgba(213, 17, 17, 0.28)"
                            : "1px solid rgba(15, 106, 45, 0.22)",
                        background:
                          notice.tone === "warning"
                            ? "rgba(213, 17, 17, 0.09)"
                            : "rgba(34, 197, 94, 0.1)",
                        color:
                          notice.tone === "warning" ? "#8a1f12" : "#0f6a2d",
                        fontWeight: 600,
                        lineHeight: 1.4,
                      }}
                    >
                      {notice.message}
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="plans-grid">
                {plans.map((plan) => {
                  const localizedPlan = getLocalizedPlan(plan, t);
                  const planId = getPlanId(plan);
                  const isCurrent = !!planId && planId === getPlanId(currentPlan);
                  const isScheduled =
                    !!planId && planId === getPlanId(scheduledPlan);
                  const featured =
                    String((plan as any).type ?? "")
                      .trim()
                      .toLowerCase() === "free";

                  let disabled = !!busyPlanId || !planId;
                  let label: string;

                  if (!isAuthenticated) {
                    label = t("pageBody.plans.selectPlan");
                  } else if (isCurrent) {
                    disabled = true;
                    label = t("pageBody.plans.currentPlan");
                  } else if (isScheduled) {
                    disabled = true;
                    label = t("checkout.actions.scheduleChange");
                  } else if (isPlanBelowUsage(plan)) {
                    disabled = true;
                    label = t("pageBody.plans.notAvailableShort");
                  } else {
                    label = t("pageBody.plans.selectPlan");
                  }

                  const tooltip = !isAuthenticated
                    ? undefined
                    : isCurrent
                      ? t("pageBody.plans.thisIsCurrent")
                      : isScheduled
                        ? billingEffectiveAtLabel
                          ? t("checkout.onScreenMessage.changeAlreadyScheduledOn", {
                              dateTime: billingEffectiveAtLabel,
                            })
                          : t("checkout.onScreenMessage.changeAlreadyScheduled")
                        : isPlanBelowUsage(plan)
                            ? t("pageBody.plans.exceedLimit")
                            : !planId
                              ? t("pageBody.plans.noPlanId")
                              : !!busyPlanId
                                ? t("common.pleaseWait")
                                : undefined;

                  return (
                    <div
                      key={planId || plan.name}
                      className={`plan-card${featured ? " featured" : ""}`}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                          marginBottom: 6,
                        }}
                      >
                        {featured ? (
                          <span className="plan-badge">
                            {t("common.featured")}
                          </span>
                        ) : null}
                        {isCurrent ? (
                          <span className="plan-badge">
                            {t("pageBody.plans.currentPlan")}
                          </span>
                        ) : null}
                        {isScheduled ? (
                          <span className="plan-badge">
                            {t("checkout.actions.scheduleChange")}
                          </span>
                        ) : null}
                      </div>

                      <h3 className="plan-name">
                        {localizedPlan.displayName ?? t("pageBody.plans.noName")}
                      </h3>
                      <p className="plan-description">
                        {localizedPlan.displayDescription ??
                          t("pageBody.plans.noDescription")}
                      </p>

                      {isScheduled && billingEffectiveAtLabel ? (
                        <p
                          style={{
                            color: "#0f6a2d",
                            fontWeight: 600,
                            marginBottom: 12,
                          }}
                        >
                          Takes effect on {billingEffectiveAtLabel}.
                        </p>
                      ) : null}

                      <div className="plan-price">
                        {formatCurrency(Number((plan as any).price ?? 0))}
                      </div>
                      <div className="plan-cycle">
                        {localizedPlan.displayBillingCycle ?? plan.billingCycle}
                      </div>

                      <ul className="plan-features">
                        <li className="messages-allowed">
                          <img className="plan-icons" src={messagesIcon} alt="" />{" "}
                          {(plan as any).messagesAllowed === 9999
                            ? t("pageBody.plans.unlimitedMessages")
                            : (plan as any).messagesAllowed === 1
                              ? `1 ${t("message.message")}`
                              : `${(plan as any).messagesAllowed ?? 0} ${t("message.messages")}`}
                        </li>
                        <li>
                          <img
                            src={recipientsIcon}
                            alt=""
                            className="plan-icons"
                          />{" "}
                          {(plan as any).recipientsAllowed === 9999
                            ? t("pageBody.plans.unlimitedRecipients")
                            : `${(plan as any).recipientsAllowed ?? 0} ${t("recipient.recipients")}`}
                        </li>
                        <li>
                          <img
                            className="plan-icons"
                            alt=""
                            src={attachmentsIcon}
                          />{" "}
                          {(plan as any).attachmentsAllowed ?? 0}{" "}
                          {t("file.attachments")}
                        </li>
                      </ul>

                      <div className="select-button-wrapper">
                        <TooltipButton
                          label={label}
                          tooltip={tooltip}
                          disabled={disabled}
                          onClick={() => void handleSelectPlan(plan)}
                          buttonClassName="select-button"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>

      <SocialResponsibility />
    </>
  );
}

export default Plans;
